import React, { useState, useRef, useEffect } from "react";
import { FaHeart, FaComment } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import { useNavigate } from "react-router-dom"; 
import { useTranslation } from "../../i18n";
import { getLocalizedBlog } from "../../utils/localizedBlog";
import { getOptimizedPublicImage, hasOptimizedPublicImage } from "../../utils/imageOptimization";
import { fetchJsonFromApiCandidates } from "../../utils/api";
import { mergeBlogsWithFallbacks, isFallbackBlog } from "../../utils/fallbackBlogs";
import { renderBrandText } from "../../utils/brandText";
import blogFallbackAvatar from "../../images/blog-fallback-avatar.png";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./BlogDetailsHome.css";

const fallbackHomeBlogs = mergeBlogsWithFallbacks([]).slice(0, 6);

function BlogDetailsHome() {
    const { language, t } = useTranslation();
    const [blogs, setBlogs] = useState(fallbackHomeBlogs);
    const [showCommentBox, setShowCommentBox] = useState({});
    const [newComment, setNewComment] = useState("");
    const progressCircle = useRef(null);
    const progressContent = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const onAutoplayTimeLeft = (s, time, progress) => {
        if (progressCircle.current && progressContent.current) {
            progressCircle.current.style.setProperty('--progress', 1 - progress);
            progressContent.current.textContent = `${Math.ceil(time / 1000)}s`;
        }
    };
    const navigate = useNavigate(); // 🔥 Hook para navegación

    useEffect(() => {
        // 🔹 Verifica si es móvil
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
    
        // 🔹 Trae los blogs más recientes
        fetchJsonFromApiCandidates("/api/blogs", { timeoutMs: 2500 })
            .then(({ data }) => {
                const latestBlogs = mergeBlogsWithFallbacks(data).slice(0, 6);

                setBlogs(latestBlogs);
            })
            .catch((error) => {
                console.error("Error al cargar blogs:", error);
                setBlogs(fallbackHomeBlogs);
            })
    
        // 🔹 Limpieza del listener
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    const handleNavigate = (id) => {
        navigate(`/blog/${id}`); // 🔥 Redirige a la página de detalles del blog
    };

    const handleLike = async (id) => {
        if (isFallbackBlog(id)) {
            setBlogs((currentBlogs) =>
                currentBlogs.map((blog) =>
                    blog._id === id ? { ...blog, likes: (blog.likes || 0) + 1 } : blog
                )
            );
            return;
        }

        try {
            const { data: updatedBlog } = await fetchJsonFromApiCandidates(`/api/blogs/${id}/like`, { method: "POST" });
            setBlogs(blogs.map(blog => (blog._id === id ? updatedBlog : blog)));
        } catch (error) {
            console.error("Error al dar like:", error);
        }
    };

    const toggleCommentBox = (id) => {
        setShowCommentBox({ ...showCommentBox, [id]: !showCommentBox[id] });
    };

    const handleAddComment = async (id) => {
        if (!newComment) return;

        if (isFallbackBlog(id)) {
            setBlogs((currentBlogs) =>
                currentBlogs.map((blog) =>
                    blog._id === id
                        ? {
                            ...blog,
                            comments: [
                                ...(Array.isArray(blog.comments) ? blog.comments : []),
                                { user: "Usuario", comment: newComment }
                            ]
                        }
                        : blog
                )
            );
            setNewComment("");
            setShowCommentBox({ ...showCommentBox, [id]: false });
            return;
        }

        try {
            const { data: updatedBlog } = await fetchJsonFromApiCandidates(`/api/blogs/${id}/comment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user: "Usuario", comment: newComment })
            });
            setBlogs(blogs.map(blog => (blog._id === id ? updatedBlog : blog)));
            setNewComment("");
            setShowCommentBox({ ...showCommentBox, [id]: false });
        } catch (error) {
            console.error("Error al añadir comentario:", error);
        }
    };

    const BlogImage = ({ blog, title }) => {
        if (!blog.image) {
            return (
                <div className="blog-image blog-image-placeholder" aria-label={title}>
                    <img src={blogFallbackAvatar} alt={title} className="blog-image-placeholder-avatar" loading="lazy" />
                </div>
            );
        }

        const optimizedImage = getOptimizedPublicImage(blog.image);
        const image = (
            <img
                src={blog.image}
                alt={title}
                className="blog-image"
                loading="lazy"
                decoding="async"
                width="900"
                height="506"
            />
        );

        if (!hasOptimizedPublicImage(blog.image)) return image;

        return (
            <picture>
                <source srcSet={optimizedImage} type="image/webp" />
                {image}
            </picture>
        );
    };

    return (
        <section className="blog-container">
            <div className="section-heading-stack section-heading-stack-center blog-heading">
                <span className="section-kicker blog-section-kicker">{t("blog.sectionKicker")}</span>
                <h2 className="blogs-title section-title-academy">{t("blog.latestTitle")}</h2>
            </div>
            <div className="blogs-info-text-home">
                <p className="blogs-info-home">
                    {renderBrandText(t("blog.homeInfo"))}
                </p>
                <p className="blogs-info-home">
                    <strong>{t("blog.homeStrong")}</strong>
                </p>
            </div>
            <div className="blog-entries">
                {blogs.length > 0 ? (
                    <Swiper
                        modules={[Pagination, Navigation, Autoplay]}
                        spaceBetween={20}
                        slidesPerView={isMobile ? 1 : Math.min(2, blogs.length)}
                        centeredSlides={false}
                        pagination={{ clickable: true }}
                        navigation={isMobile ? false : blogs.length > 1} // 🔥 Botones de navegación (siguiente/anterior)
                        autoplay={{ delay: 3000, disableOnInteraction: false }} // 🔥 Autoplay cada 3 segundos
                        loop={blogs.length > 1} // 🔥 Hace que los slides se repitan en un bucle
                        style={{ width: "100%", maxWidth: "1200px", height: "auto" }}
                        onAutoplayTimeLeft={onAutoplayTimeLeft}
                        className="blog-swiper"
                    >
                        {blogs.map((blog) => {
                            const localizedBlog = getLocalizedBlog(blog, language);

                            return (
                            <SwiperSlide key={blog._id}>
                                <div className="blog-card">
                                    <BlogImage blog={blog} title={localizedBlog.title} />
                                    <div className="blog-content-home">
                                        <h3>{localizedBlog.title}</h3>
                                        <p className="blog-date-home">{t("common.publishedOn")} {new Date(blog.createdAt).toLocaleDateString()}</p>
                                        <p className="blog-summary-home">{localizedBlog.summary}</p>
                                        <div className="blog-actions">
                                            <button className="like-button" onClick={() => handleLike(blog._id)}>
                                                <FaHeart /> <span>{blog.likes} {t("common.likes")}</span>
                                            </button>
                                            <button className="comment-button" onClick={() => toggleCommentBox(blog._id)}>
                                                <FaComment /> {t("common.comments")}
                                            </button>
                                        </div>
                                        {showCommentBox[blog._id] && (
                                            <div className="comment-box">
                                                <input 
                                                    type="text" 
                                                    placeholder={t("blog.commentPlaceholder")} 
                                                    value={newComment} 
                                                    onChange={(e) => setNewComment(e.target.value)}
                                                />
                                                <button onClick={() => handleAddComment(blog._id)}>{t("common.send")}</button>
                                            </div>
                                        )}
                                        <button className="read-more" onClick={() => handleNavigate(blog._id)}>
                                            {t("common.readMore")}
                                        </button>
                                    </div>
                                </div>
                            </SwiperSlide>
                        )})}
                    </Swiper>
                ) : (
                    <p className="loading">{t("blog.noResultsHome")}</p>
                )}
            </div>
        </section>
    );
}

export default BlogDetailsHome;
